import { useEffect } from "react";
import { Link } from "react-router-dom";
import {
  cubeLogo,
  aboutData,
  consultationLink,
  contactInfo,
  testimonials,
} from "../data/mock";
import { Badge } from "../components/ui/badge";
import { Button } from "../components/ui/button";
import {
  ArrowRight,
  ArrowUpRight,
  Check,
  Star,
  Clock,
  Video,
  Shield,
  Users,
  ChevronRight,
  Phone,
  Calendar,
  Zap,
} from "lucide-react";
import { motion } from "framer-motion";

export default function ConsultationPage() {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const benefits = [
    {
      icon: Zap,
      title: "Custom Growth Strategy",
      desc: "Get a personalized marketing roadmap built specifically for your brand and business goals.",
    },
    {
      icon: Users,
      title: "Senior Expert Guidance",
      desc: "1-on-1 session with our senior marketing consultants with 7+ years of experience.",
    },
    {
      icon: Video,
      title: "Live Strategy Session",
      desc: "A focused video call where we analyze your brand, competitors, and opportunities.",
    },
    {
      icon: Shield,
      title: "Actionable Insights",
      desc: "Walk away with a clear action plan you can implement immediately.",
    },
  ];

  const processSteps = [
    {
      step: "01",
      title: "Book Your Slot",
      desc: "Choose a convenient time slot from the calendar.",
    },
    {
      step: "02",
      title: "Pre-Call Brief",
      desc: "Share your business details so we come prepared.",
    },
    {
      step: "03",
      title: "Strategy Call",
      desc: "Deep-dive session with our senior consultant.",
    },
    {
      step: "04",
      title: "Action Plan",
      desc: "Receive your customized growth roadmap.",
    },
  ];

  return (
    <div>
      {/* ===== HERO ===== */}
      <section className="relative min-h-[85vh] flex items-center overflow-hidden pt-20">
        <div className="absolute inset-0 pointer-events-none">
          <div
            className="absolute inset-0 opacity-[0.03]"
            style={{
              backgroundImage:
                "linear-gradient(rgba(255,255,255,0.1) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,0.1) 1px, transparent 1px)",
              backgroundSize: "60px 60px",
            }}
          />
          <motion.div
            animate={{ scale: [1, 1.3, 1], opacity: [0.15, 0.25, 0.15] }}
            transition={{ duration: 8, repeat: Infinity, ease: "easeInOut" }}
            className="absolute top-1/3 right-1/3 w-[600px] h-[600px] rounded-full bg-[#dc2626] blur-[200px]"
          />
        </div>

        <div className="relative max-w-7xl mx-auto px-6 lg:px-8 w-full">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            {/* Left - Content */}
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.6 }}
            >
              <div className="flex items-center gap-2 text-[#a1a1aa] text-sm mb-6">
                <Link to="/" className="hover:text-white transition-colors">Home</Link>
                <ChevronRight className="w-3.5 h-3.5" />
                <span className="text-white">Consultation</span>
              </div>

              <Badge
                variant="outline"
                className="border-[#dc2626]/30 bg-[#dc2626]/[0.08] text-[#ef4444] px-4 py-1.5 text-sm rounded-full mb-6"
              >
                Limited Slots Available
              </Badge>

              <h1
                className="text-4xl sm:text-5xl lg:text-6xl font-bold text-white leading-tight tracking-tight mb-6"
                style={{ fontFamily: "'Space Grotesk', sans-serif" }}
              >
                1-on-1 Strategy Call
                <br />
                <span className="text-transparent bg-clip-text bg-gradient-to-r from-[#dc2626] to-[#ef4444]">
                  With Senior Experts
                </span>
              </h1>

              <p className="text-lg text-[#a1a1aa] mb-8 max-w-lg">
                Get a personalized marketing strategy session with Cube's senior consultants. We'll analyze your brand, identify opportunities, and build a roadmap for growth.
              </p>

              <div className="flex flex-wrap items-center gap-4 mb-8">
                <a
                  href={consultationLink}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  <Button className="bg-[#dc2626] hover:bg-[#b91c1c] text-white px-10 py-6 rounded-xl text-base font-semibold transition-colors duration-300 flex items-center gap-2 shadow-lg shadow-[#dc2626]/20">
                    Book Your Consultation
                    <ArrowRight className="w-5 h-5" />
                  </Button>
                </a>
                <a
                  href={`https://wa.me/${contactInfo.whatsapp.replace(/[^0-9]/g, "")}`}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  <Button
                    variant="outline"
                    className="border-white/[0.12] bg-white/[0.03] hover:bg-white/[0.06] text-white px-8 py-6 rounded-xl text-base font-medium transition-colors duration-300 flex items-center gap-2"
                  >
                    <Phone className="w-4 h-4" />
                    WhatsApp Us
                  </Button>
                </a>
              </div>

              <div className="flex items-center gap-4">
                <div className="flex items-center gap-1">
                  {[...Array(5)].map((_, i) => (
                    <Star key={i} className="w-4 h-4 fill-[#dc2626] text-[#dc2626]" />
                  ))}
                </div>
                <span className="text-[#a1a1aa] text-sm">
                  Rated 4.9/5 by 200+ clients
                </span>
              </div>
            </motion.div>

            {/* Right - Visual Card */}
            <motion.div
              initial={{ opacity: 0, x: 30 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.6, delay: 0.2 }}
              className="relative"
            >
              <div className="relative p-8 rounded-3xl bg-white/[0.03] border border-white/[0.08] backdrop-blur-sm">
                <div className="flex items-center gap-3 mb-8">
                  <div className="w-12 h-12 rounded-xl bg-[#dc2626] flex items-center justify-center">
                    <Calendar className="w-6 h-6 text-white" />
                  </div>
                  <div>
                    <h3 className="text-white font-semibold text-lg">Strategy Call</h3>
                    <p className="text-[#71717a] text-sm">Prepaid Consultation</p>
                  </div>
                </div>

                <div className="space-y-4 mb-8">
                  {[
                    { icon: Clock, text: "30-45 min deep-dive session" },
                    { icon: Video, text: "Video call with screen sharing" },
                    { icon: Users, text: "Senior marketing consultant" },
                    { icon: Zap, text: "Actionable growth roadmap" },
                  ].map(({ icon: Icon, text }, i) => (
                    <div key={i} className="flex items-center gap-3">
                      <div className="w-8 h-8 rounded-lg bg-[#dc2626]/10 flex items-center justify-center shrink-0">
                        <Icon className="w-4 h-4 text-[#dc2626]" />
                      </div>
                      <span className="text-[#a1a1aa] text-sm">{text}</span>
                    </div>
                  ))}
                </div>

                <a
                  href={consultationLink}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="block"
                >
                  <Button className="w-full bg-[#dc2626] hover:bg-[#b91c1c] text-white py-5 rounded-xl text-sm font-semibold transition-colors duration-300 flex items-center justify-center gap-2">
                    Book Now
                    <ArrowUpRight className="w-4 h-4" />
                  </Button>
                </a>

                <p className="text-center text-[#52525b] text-xs mt-4">
                  Prepaid booking — Secure your slot today
                </p>

                {/* Glow */}
                <div className="absolute -top-6 -right-6 w-40 h-40 bg-[#dc2626]/15 rounded-full blur-[80px] pointer-events-none" />
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* ===== BENEFITS ===== */}
      <section className="py-20 lg:py-28">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="text-center mb-16"
          >
            <h2
              className="text-3xl lg:text-4xl font-bold text-white mb-3"
              style={{ fontFamily: "'Space Grotesk', sans-serif" }}
            >
              What You'll <span className="text-[#dc2626]">Get</span>
            </h2>
            <p className="text-[#71717a] text-base max-w-lg mx-auto">
              Every consultation is designed to give you maximum clarity and a clear path forward.
            </p>
          </motion.div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {benefits.map((b, i) => (
              <motion.div
                key={i}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: i * 0.1 }}
                className="p-6 rounded-2xl bg-white/[0.02] border border-white/[0.06] hover:border-[#dc2626]/20 transition-all duration-500 text-center group"
              >
                <div className="w-14 h-14 rounded-xl bg-[#dc2626]/10 flex items-center justify-center mb-5 mx-auto group-hover:bg-[#dc2626]/20 transition-colors duration-300">
                  <b.icon className="w-7 h-7 text-[#dc2626]" />
                </div>
                <h3 className="text-white font-semibold mb-2">{b.title}</h3>
                <p className="text-[#52525b] text-sm leading-relaxed">{b.desc}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* ===== PROCESS ===== */}
      <section className="py-20 lg:py-28">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="text-center mb-16"
          >
            <h2
              className="text-3xl lg:text-4xl font-bold text-white mb-3"
              style={{ fontFamily: "'Space Grotesk', sans-serif" }}
            >
              How It <span className="text-[#dc2626]">Works</span>
            </h2>
          </motion.div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {processSteps.map((s, i) => (
              <motion.div
                key={i}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: i * 0.1 }}
                className="relative p-6 rounded-2xl bg-white/[0.02] border border-white/[0.06]"
              >
                <span
                  className="text-5xl font-black text-[#dc2626]/10 absolute top-4 right-4"
                  style={{ fontFamily: "'Space Grotesk', sans-serif" }}
                >
                  {s.step}
                </span>
                <div className="relative">
                  <h3 className="text-white font-semibold mb-2 mt-8">{s.title}</h3>
                  <p className="text-[#52525b] text-sm">{s.desc}</p>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* ===== TESTIMONIALS ===== */}
      <section className="py-20 lg:py-28">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <h2
            className="text-3xl lg:text-4xl font-bold text-white mb-12 text-center"
            style={{ fontFamily: "'Space Grotesk', sans-serif" }}
          >
            Trusted by <span className="text-[#dc2626]">200+ Clients</span>
          </h2>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-12">
            {testimonials.slice(0, 3).map((t, i) => (
              <motion.div
                key={t.id}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: i * 0.1 }}
                className="p-6 rounded-2xl bg-white/[0.03] border border-white/[0.08]"
              >
                <div className="flex items-center gap-1 mb-3">
                  {[...Array(t.rating)].map((_, j) => (
                    <Star key={j} className="w-4 h-4 fill-[#dc2626] text-[#dc2626]" />
                  ))}
                </div>
                <p className="text-[#a1a1aa] text-sm leading-relaxed mb-4">
                  &ldquo;{t.review}&rdquo;
                </p>
                <div className="flex items-center gap-3">
                  <div className="w-8 h-8 rounded-full bg-[#dc2626]/20 flex items-center justify-center text-[#dc2626] font-bold text-xs">
                    {t.initial}
                  </div>
                  <span className="text-white text-sm font-medium">{t.name}</span>
                </div>
              </motion.div>
            ))}
          </div>

          {/* Final CTA */}
          <div className="text-center">
            <a
              href={consultationLink}
              target="_blank"
              rel="noopener noreferrer"
            >
              <Button className="bg-[#dc2626] hover:bg-[#b91c1c] text-white px-12 py-6 rounded-xl text-lg font-semibold transition-colors duration-300 flex items-center gap-3 mx-auto shadow-lg shadow-[#dc2626]/20">
                Book Your Strategy Call Now
                <ArrowRight className="w-5 h-5" />
              </Button>
            </a>
            <p className="text-[#52525b] text-sm mt-4">
              Limited slots available — Don't miss out!
            </p>
          </div>
        </div>
      </section>
    </div>
  );
}
